import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog.jsx';
import { formatHumanDate } from '@/lib/date-display.js';
import { fileDealDocuments } from '@/services/deals.service.js';
import { DROP_KINDS, BUNDLE_LINE, dropKind } from '@/lib/dropKinds.js';
import { DOCUMENT_ERROR_WORDS, word } from './words.js';

// THE CONFIRM SHEET for a file dropped on the account (paperwork-1 L1b-B1: repointed to the deal's document door, D7). The
// broker says what the file is (the drop's kinds, each with its one plain line), which term it goes on and, for a
// certificate, which holder it names; the sheet files it through fileDealDocuments on that term's deal (fileDocument on its
// policy) and hands the caller the server's answer, the stored row with its derived name. When the server asks "Does this
// replace <name>?" the sheet asks it here and files again with the answer. It derives nothing; every error is the server's
// sentence or code, in place. A quote is filed from the deal page (the quote parse), not here.

const KINDS = DROP_KINDS.filter((k) => k.kind !== 'quote');

// The term options as the sketch words them: the carrier, the period, the policy number when stated.
function termLabel(t) {
  const p = t.period || {};
  const s = formatHumanDate(p.effective_date); const e = formatHumanDate(p.expiration_date);
  const period = s && e ? `${s} to ${e}` : (s || e || 'period not recorded');
  return [t.carrier?.name || 'carrier not stated', period, t.policy_number].filter(Boolean).join(' · ');
}

export default function DocumentConfirmSheet({ item, account, terms = [], interests = [], currentTermId = null, onCancel, onConfirmed }) {
  const [kind, setKind] = useState(null);
  const [policyId, setPolicyId] = useState(currentTermId || terms[0]?.id || '');
  const [interestId, setInterestId] = useState('');
  const [ask, setAsk] = useState(null); // the server's replace candidates
  const [replaces, setReplaces] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const open = !!item;
  const term = terms.find((t) => t.id === policyId) || null;
  const k = dropKind(kind);

  const file = async () => {
    if (!kind || saving || !item?.file) return;
    if (!term?.crm_deal_id) { setError('This term has no deal to file on; file it from the deal page.'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = await fileDealDocuments(term.crm_deal_id, [{ file: item.file, kind, interest_id: kind === 'certificate' ? interestId : undefined, replaces_document_id: ask ? replaces : undefined }]);
      const r = body.results[0] || {};
      if (r.code === 'REPLACES_UNANSWERED') { setAsk(r.candidates || []); setReplaces(undefined); return; }
      if (!r.ok) { setError(r.error || r.code || 'not filed'); return; }
      onConfirmed({ document: r.document, name: r.document ? r.document.name : r.name, application: r.application || null });
    } catch (err) { setError(err?.code || err?.message || 'filing failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent className="max-w-xl" data-document-confirm={item?.key || 'none'}>
        <DialogHeader>
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">Dropped on {account?.name || 'this account'}</div>
          <DialogTitle data-guess-headline>What is {item?.name || 'this file'}?</DialogTitle>
          <DialogDescription>Files it on the term you choose, under the kind you choose. Nothing is stored until you file it.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What this is" data-class-chips>
          {KINDS.map((c) => (
            <button key={c.kind} type="button" role="radio" aria-checked={kind === c.kind} onClick={() => { setKind(c.kind); setAsk(null); }} className={`min-h-[40px] rounded-pill border px-3.5 text-[13px] font-medium ${kind === c.kind ? 'border-accent bg-accent-soft text-accent' : 'border-border bg-surface text-text hover:border-border-strong'}`} data-class-chip={c.kind}>{c.label}</button>
          ))}
        </div>
        {k ? <p className="text-xs text-text-muted" data-kind-line={k.kind}>{k.label}: {k.line}</p> : <p className="text-xs text-text-dim">{BUNDLE_LINE}</p>}

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">Goes on</span>
          <select aria-label="Policy" value={policyId} onChange={(e) => { setPolicyId(e.target.value); setAsk(null); }} disabled={saving} className="min-h-[40px] rounded-card border border-border bg-surface px-2.5 text-sm text-text" data-goes-on>
            {terms.map((t) => <option key={t.id} value={t.id}>{termLabel(t)}</option>)}
          </select>
        </label>

        {kind === 'certificate' ? (
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">Names</span>
            <select aria-label="Holder" value={interestId} onChange={(e) => { setInterestId(e.target.value); setAsk(null); }} disabled={saving} className="min-h-[40px] rounded-card border border-border bg-surface px-2.5 text-sm text-text" data-names-interest>
              <option value="">Nobody on the interests list</option>
              {interests.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        ) : null}

        {ask && ask.length ? (
          <fieldset className="rounded-card border border-border px-3 py-2" data-replace-question>
            <legend className="px-1 text-[12px] font-medium text-text">Does this replace {ask.length === 1 ? ask[0].name : 'one already filed'}?</legend>
            <label className="flex min-h-[36px] items-center gap-2 text-sm text-text"><input type="radio" name="replaces" checked={replaces === 'none'} onChange={() => setReplaces('none')} disabled={saving} />No, it is another document</label>
            {ask.map((c) => <label key={c.id} className="flex min-h-[36px] items-center gap-2 text-sm text-text"><input type="radio" name="replaces" checked={replaces === c.id} onChange={() => setReplaces(c.id)} disabled={saving} />Yes, it replaces {c.name}</label>)}
          </fieldset>
        ) : null}

        {error ? <p className="text-sm text-danger" data-confirm-error={error}>{word(DOCUMENT_ERROR_WORDS, error)}</p> : null}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={saving} className="min-h-[40px] rounded-card px-3 text-sm text-text-muted hover:text-text disabled:opacity-60">Cancel</button>
          <button type="button" onClick={file} disabled={!kind || saving || !!(ask && ask.length && replaces === undefined)} className="min-h-[40px] rounded-card border border-accent bg-accent px-4 text-sm font-medium text-white disabled:opacity-60" data-file-as={kind || 'none'}>{saving ? 'Filing' : `File as ${k ? k.label.toLowerCase() : '...'}`}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
